/**
 * The Activity read, and the client-side filter it walks past — extracted from
 * `ActivityScreen.tsx` so the Access page can **prefetch** the first page on entry (R5 step 6)
 * without importing a screen component, and so the walk stays one implementation rather than two.
 *
 * Nothing here changed in the move. The filter still runs at the render (`isVisible` is exported
 * for that one caller) and the walk still keeps hidden rows in what it returns, because a second
 * filtering site is how the two would come to disagree about what is hidden.
 */
import type { GateId } from '@selogate/core'
import { NetworkError } from '../../auth/transport'
import type { ApiRequestFn } from '../shared/api'
import { getGateActivity, type ActivityRow } from '../shared/api'

/**
 * §5 / P9 note — system-observation rows, never a person's action; filtered on the client.
 *
 * **Two members, and they are the same kind of thing** (D243): a probe the platform ran, and a
 * credential the platform pre-fetched. Neither was tapped. See the header for what `ble-access`
 * costs and why it is excluded whole.
 *
 * **The operator's READS are hidden on the same argument** — `admin-pii-read` and `admin-export`
 * record that the Monitor looked, and nothing happened at the gate. They reached this screen once
 * D336 made the Monitor's gate views write gate-scoped rows: one Monitor gate page wrote six in
 * half a second on production, each rendered as a nameless "activity" row by an "unknown" user,
 * because the operator is no member of the gate. **`admin-mutate` stays visible** — that one
 * changed something the manager owns. The rows themselves are untouched; this surface simply does
 * not render them.
 */
const HIDDEN_ACTIONS = new Set(['status-probe', 'ble-access', 'admin-pii-read', 'admin-export'])

/** Whether the Activity screen will render a row at all — the one place {@link HIDDEN_ACTIONS} is read. */
export function isVisible(row: ActivityRow): boolean {
  return !HIDDEN_ACTIONS.has(row.action)
}

/**
 * 🔸 **How many pages this screen pulls through in one go while the filter eats every row.**
 *
 * `HIDDEN_ACTIONS` runs on the client against a page the server has already sized —
 * `DEFAULT_PAGE_SIZE`, 30 rows — because §5 forbids `query.ts` dropping a row by `action`. So a
 * page can arrive composed **entirely** of rows this screen will not render, and D243 is what made
 * that reachable rather than theoretical: `status-probe` had the same exposure, but D142 left only
 * two probe callers, while `warmCredential` writes one row per Gate Detail view and up to three per
 * Home render (D189(e)) — thirty in a day on a gate with a few active residents.
 *
 * **Without this walk the screen states the opposite of the truth and offers no way out of it.**
 * The `rows.length === 0` branch is checked *before* the list, and the *load more* control lives
 * *inside* the list, so an all-hidden page rendered *"no activity yet"* over a log that had plenty,
 * with no control to reach the page behind it. That is a dead end rather than a thin page, and it
 * is the half D243's own 🔸 cost clause originally described too kindly.
 *
 * **Four rounds is 120 rows** — enough to clear a realistic run of warms, small enough that a
 * manager never waits on an unbounded walk of a twelve-month log. **The bound is the point, and it
 * is the same shape `query.ts` uses one layer down**, whose `MAX_ROUNDS` exists so that a highly
 * selective filter cannot spin. When the rounds run out with nothing visible and a cursor still in
 * hand, the render hands the manager the *load more* control **in the empty state** rather than
 * walking further on its own — the two halves are belt and braces, and neither is sufficient: the
 * walk alone still dead-ends past 120 hidden rows, and the control alone makes a manager tap
 * through empty pages by hand.
 */
const AUTO_ADVANCE_ROUNDS = 4

/**
 * One page as the Activity screen wants it: far enough into the log that **something is renderable**, or
 * the rounds spent trying.
 *
 * Hidden rows are kept in the returned set rather than dropped here. The filter belongs at the
 * render, `state.rows` stays the server's own answer, and a second filtering site is how the two
 * would come to disagree about what is hidden.
 */
export type FetchedPage =
  | { readonly kind: 'page'; readonly rows: readonly ActivityRow[]; readonly nextCursor?: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'failed' }

export async function fetchActivityPage(
  request: ApiRequestFn,
  gateId: GateId,
  cursor: string | undefined,
): Promise<FetchedPage> {
  const collected: ActivityRow[] = []
  let next = cursor
  for (let round = 0; round < AUTO_ADVANCE_ROUNDS; round += 1) {
    let result
    try {
      result = await getGateActivity(request, gateId, next === undefined ? {} : { cursor: next })
    } catch (error) {
      // Same rule as before: only a network failure is a `failed` phase; anything else is a bug
      // and is rethrown rather than rendered as a retry the user cannot succeed at.
      if (!(error instanceof NetworkError)) throw error
      return { kind: 'failed' }
    }
    if (!result.ok) return result.status === 403 ? { kind: 'forbidden' } : { kind: 'failed' }
    collected.push(...result.data.rows)
    next = result.data.nextCursor
    /*
     * **The exit is `some(isVisible)` over what THIS call collected, never over the screen's
     * existing rows.** On `loadMore` the screen already holds visible rows, so a test against the
     * whole list would satisfy itself immediately and the tap would append nothing anyone can see —
     * which is the same dead end one interaction later.
     */
    if (next === undefined || collected.some(isVisible)) break
  }
  return { kind: 'page', rows: collected, ...(next === undefined ? {} : { nextCursor: next }) }
}
